/**
 * LuxeSteam video — two-state lazy player.
 *
 * States:
 *   - preview: muted, looping, autoplay starts only when the section
 *              enters the viewport. Play button overlay visible.
 *   - sound:   muted=false, loop off, native HTML controls visible,
 *              play button hidden. User explicitly opted in.
 *
 * Optimisation:
 *   - <video preload="metadata"> + no `autoplay` attr in HTML →
 *     no body data is fetched until JS triggers play() once the
 *     element scrolls into view. Saves ~bandwidth on initial load.
 */
(() => {
  if (window.__luxeVideoInit) return;
  window.__luxeVideoInit = true;

  class LuxeVideo {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.video = wrapper.querySelector('.luxe-section__video');
      this.btn = wrapper.querySelector('.luxe-section__play-btn');
      this.state = 'preview';
      this.inViewport = false;

      if (!this.video) return;

      // Ensure attributes are right (defensive, overrides any inline edits)
      this.video.muted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.video.controls = false;
      this.video.removeAttribute('autoplay');
      if (!this.video.getAttribute('preload')) {
        this.video.setAttribute('preload', 'metadata');
      }

      this.bind();
      this.observe();
    }

    bind() {
      if (this.btn) {
        this.btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.enterSoundMode();
        });
      }
    }

    observe() {
      // No IO support → just try to play immediately (graceful fallback).
      if (typeof IntersectionObserver === 'undefined') {
        this.tryPlay();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.inViewport = true;
              if (this.state === 'preview') this.tryPlay();
            } else {
              this.inViewport = false;
              if (this.state === 'preview') this.video.pause();
              // If in sound mode and user scrolls away — let them keep listening.
              // Comment out next line to keep video playing off-screen.
              if (this.state === 'sound') this.video.pause();
            }
          });
        },
        { threshold: 0.25 }
      );
      this.observer.observe(this.wrapper);
    }

    tryPlay() {
      const p = this.video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          // Autoplay blocked (rare for muted, can happen on low-power mode).
          // Leave play button visible — user can tap it.
        });
      }
    }

    enterSoundMode() {
      this.state = 'sound';
      this.video.muted = false;
      this.video.loop = false;
      this.video.controls = true;
      this.video.currentTime = 0;
      if (this.btn) this.btn.classList.add('is-hidden');

      // Pause any other sound-mode videos on the page
      document.querySelectorAll('[data-luxe-video]').forEach((other) => {
        if (other === this.wrapper) return;
        const inst = other.__luxeVideoInstance;
        if (inst && inst.state === 'sound') inst.exitSoundMode();
      });

      this.tryPlay();
    }

    exitSoundMode() {
      this.state = 'preview';
      this.video.muted = true;
      this.video.loop = true;
      this.video.controls = false;
      if (this.btn) this.btn.classList.remove('is-hidden');
      if (this.inViewport) this.tryPlay();
    }

    destroy() {
      this.observer?.disconnect();
      this.video?.pause();
    }
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-luxe-video]').forEach((wrapper) => {
      if (wrapper.__luxeVideoInstance) return;
      wrapper.__luxeVideoInstance = new LuxeVideo(wrapper);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Re-init on Shopify theme editor reloads
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-luxe-video]').forEach((wrapper) => {
      wrapper.__luxeVideoInstance?.destroy();
      wrapper.__luxeVideoInstance = null;
    });
  });
})();
